/**
 * Option sources for the segment builder's `ref` fields.
 *
 * A `SegmentField` with `type: 'ref'` names an EXISTING option source by a
 * string (`company-stages`, `countries`, …). This module is the one place that
 * string is turned into a list of `{value, label}` options, always through a
 * method `api.ts` already has — nothing here invents an endpoint.
 *
 * Three rules worth keeping:
 *
 * - **A filter offers what can MATCH, so retired rows are listed** (with a hint)
 *   rather than dropped. A segment saved against a retired stage must still
 *   render that stage by name.
 * - **Resolved lists are cached for the life of the tab**, keyed by ref. Every
 *   rule editor on screen asks for its own field's list, and a builder with ten
 *   rules on `company.stage` would otherwise read the same master ten times.
 *   Only SUCCESSFUL reads are cached, so a failed read is retried on the next
 *   mount rather than remembered.
 * - **`numeric` says how a value goes on the wire.** Every source keyed by a
 *   database id sends numbers; billing regions are slugs and send strings. The
 *   MultiCombobox works in strings either way.
 */
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { TARGET_BILLING_REGIONS, NO_REGION_LABEL, BILLING_REGION_NONE } from '@/lib/billing-region';
import type { MultiComboboxOption } from '@/components/ui';
import type { SegmentField, SegmentOp, SegmentRule } from '@/lib/types';

/* ---- Rule shape helpers (kept here so the component files export components only) ---- */

export const NO_VALUE_OPS: ReadonlySet<SegmentOp> = new Set(['is_empty', 'is_not_empty', 'is_true', 'is_false']);
export const LIST_OPS: ReadonlySet<SegmentOp> = new Set(['in', 'not_in']);

/** The empty value an operator starts from: `null`, `[]`, a day count or `''`. */
export function defaultValueFor(op: SegmentOp): unknown {
  if (NO_VALUE_OPS.has(op)) return null;
  if (LIST_OPS.has(op)) return [];
  if (op === 'within_last_days') return 30;
  return '';
}

/** A fresh rule on a field, using the field's first operator. */
export function defaultRuleFor(field: SegmentField): SegmentRule {
  const op: SegmentOp = field.ops[0] ?? 'is_not_empty';
  return { field: field.key, op, value: defaultValueFor(op) };
}

/** A value that settles `ms` after it last changed — the search boxes use 250ms. */
export function useDebounced<T>(value: T, ms = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(handle);
  }, [value, ms]);
  return debounced;
}

export interface RefOptions {
  options: MultiComboboxOption[];
  /** True when the value on the wire is a number (a database id). */
  numeric: boolean;
}

interface Row {
  id: number;
  label: string;
  isActive?: boolean;
}

function fromRows(rows: readonly Row[]): RefOptions {
  return {
    numeric: true,
    options: rows.map((row) => ({
      value: String(row.id),
      label: row.label,
      ...(row.isActive === false ? { hint: 'Retired' } : {}),
    })),
  };
}

type Loader = (signal: AbortSignal) => Promise<RefOptions>;

const LOADERS: Record<string, Loader> = {
  'company-stages': async (signal) =>
    fromRows(
      (await api.listCompanyStages({ perPage: 200 }, signal)).data.map((r) => ({
        id: r.id,
        label: r.name,
        isActive: r.isActive,
      })),
    ),
  countries: async (signal) =>
    fromRows(
      (await api.listCountries({ perPage: 250 }, signal)).data.map((r) => ({
        id: r.id,
        label: r.countryName,
        isActive: r.isActive,
      })),
    ),
  'billing-regions': async (signal) => {
    // The names are admin-editable on the master, so they are FETCHED; a failed
    // read falls back to the slug rather than to a literal map in this file.
    let names = new Map<string, string>();
    try {
      const master = await api.listBillingRegionDefaults(signal);
      names = new Map(master.data.map((row) => [row.slug as string, row.name]));
    } catch (error) {
      if (signal.aborted) throw error;
    }
    return {
      numeric: false,
      options: TARGET_BILLING_REGIONS.map((slug) => ({
        value: slug,
        label: slug === BILLING_REGION_NONE ? NO_REGION_LABEL : (names.get(slug) ?? slug),
      })),
    };
  },
  'company-sources': async (signal) =>
    fromRows(
      (await api.listCompanySources({ perPage: 200 }, signal)).data.map((r) => ({
        id: r.id,
        label: r.name,
        isActive: r.isActive,
      })),
    ),
  'company-types': async (signal) =>
    fromRows(
      (await api.listCompanyTypes({ perPage: 200 }, signal)).data.map((r) => ({
        id: r.id,
        label: r.name,
        isActive: r.isActive,
      })),
    ),
  faculties: async (signal) =>
    fromRows(
      (await api.listFaculties(signal)).data.map((r) => ({
        id: r.id,
        label: r.name,
        isActive: r.isActive,
      })),
    ),
  users: async (signal) =>
    fromRows(
      (await api.listAllUsers({}, signal)).data.map((u) => ({
        id: u.id,
        label: u.name.trim() || u.email,
      })),
    ),
  regions: async (signal) =>
    fromRows(
      (await api.listRegions({ perPage: 200 }, signal)).data.map((r) => ({
        id: r.id,
        label: r.name,
        isActive: r.isActive,
      })),
    ),
  projects: async (signal) =>
    fromRows(
      (await api.listAllProjects(signal)).map((p) => ({
        id: p.id,
        label: p.name,
        isActive: p.isActive,
      })),
    ),
  'contact-groups': async (signal) =>
    fromRows(
      (await api.listContactGroups({ perPage: 200 }, signal)).data.map((r) => ({
        id: r.id,
        label: r.name,
        isActive: r.isActive,
      })),
    ),
  'lead-sources': async (signal) =>
    fromRows(
      (await api.listLeadSources({ perPage: 100 }, signal)).data.map((r) => ({
        id: r.id,
        label: r.name,
        isActive: r.isActive,
      })),
    ),
  products: async (signal) =>
    fromRows(
      (await api.listAllProducts(signal)).map((p) => ({
        id: p.id,
        label: p.productName,
        isActive: p.productActive,
      })),
    ),
  cycles: async (signal) =>
    fromRows(
      (await api.listCycles({ perPage: 100 }, signal)).data.map((c) => ({
        id: c.id,
        label: c.cycleName,
      })),
    ),
  'deal-stages': async (signal) =>
    fromRows(
      (await api.listDealStages({ perPage: 100 }, signal)).data.map((r) => ({
        id: r.id,
        label: r.name,
        isActive: r.isActive,
      })),
    ),
  'marketing-campaigns': async (signal) =>
    fromRows(
      (await api.listMarketingCampaigns({ perPage: 100 }, signal)).data.map((c) => ({
        id: c.id,
        label: c.name,
      })),
    ),
  // STATIC lists only: `marketing.in_list` / `not_in_list` refuse a segment, so
  // offering one would be a choice whose only outcome is a 422.
  'marketing-lists': async (signal) =>
    fromRows(
      (await api.listMarketingLists({ kind: 'static', perPage: 100 }, signal)).data.map((l) => ({
        id: l.id,
        label: l.name,
        isActive: !l.isArchived,
      })),
    ),
};

/** Whether a ref name has a source here. An unknown ref renders a refusal, never an empty picker. */
export function isKnownRef(ref: string | undefined | null): boolean {
  return ref != null && Object.prototype.hasOwnProperty.call(LOADERS, ref);
}

const cache = new Map<string, RefOptions>();

export interface RefOptionsState {
  data: RefOptions | null;
  loading: boolean;
  failed: boolean;
}

/** Options for one `ref` source. `ref` undefined → nothing is fetched. */
export function useRefOptions(ref: string | undefined | null): RefOptionsState {
  const cached = ref ? (cache.get(ref) ?? null) : null;
  const [state, setState] = useState<{ ref: string | null; data: RefOptions | null; failed: boolean }>({
    ref: ref ?? null,
    data: cached,
    failed: false,
  });

  useEffect(() => {
    // A cached list is read during render (below), so a hit needs no fetch.
    if (!ref || !isKnownRef(ref) || cache.has(ref)) return;
    const controller = new AbortController();
    const loader = LOADERS[ref];
    if (!loader) return;
    loader(controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        cache.set(ref, data);
        setState({ ref, data, failed: false });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setState({ ref, data: null, failed: true });
      });
    return () => controller.abort();
  }, [ref]);

  if (!ref) return { data: null, loading: false, failed: false };
  if (!isKnownRef(ref)) return { data: null, loading: false, failed: true };
  const current = state.ref === ref ? state : { data: cached, failed: false };
  const data = current.data ?? cached;
  return {
    data,
    loading: data === null && !current.failed,
    failed: current.failed,
  };
}
